import PropTypes from "prop-types";
import { useCallback, useMemo, useState, useEffect } from "react";
import {
  Box,
  List,
  Drawer,
  Tooltip,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Collapse,
} from "@mui/material";
import { NavLink, useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import useUserStore from "store/zustand/user";
import GroupIcon from "@mui/icons-material/Group";
import ReportIcon from "@mui/icons-material/Report";
import {
  Chat,
  Inventory,
  LogoutOutlined,
} from "@mui/icons-material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AirlineSeatReclineNormalOutlinedIcon from "@mui/icons-material/AirlineSeatReclineNormalOutlined";
import useLocalizationStore from "store/zustand/localization";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import EmojiTransportationIcon from "@mui/icons-material/EmojiTransportation";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { chatTabs } from "data/chatData";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";

export const NAV_ITEMS = [
  {
    key: "dashboard",
    path: "/",
    icon: <DashboardOutlinedIcon />,
    roles: ["admin", "sub_admin", "staff", "Driver"],
    requiresPermission: null,
    hideForSupplier: true, // Add this flag
  },
  {
    key: "supplierDashboard",
    path: "/supplier-dashboard",
    icon: <DashboardOutlinedIcon />,
    roles: [], // Empty roles - only shown for suppliers
    requiresPermission: null,
    showOnlyForSupplier: true, // Add this flag
  },

  {
    key: "order",
    path: "/order",
    icon: <AssignmentTurnedInOutlinedIcon />,
    roles: ["admin", "sub_admin", "staff", "Driver"],
    requiresPermission: "Orders",
  },
  {
    key: "driver",
    path: "/driver",
    icon: <AirlineSeatReclineNormalOutlinedIcon />,
    roles: ["admin", "sub_admin", "staff", "Driver"],
    requiresPermission: "Drivers",
  },
  {
    key: "vehicles",
    path: "/vehicles",
    icon: <LocalShippingIcon />,
    roles: ["admin", "sub_admin", "staff", "Driver"],
    requiresPermission: "Vehicles",
  },
  {
    key: "invoice",
    path: "/invoice",
    icon: <ReceiptIcon />,
    roles: ["admin", "sub_admin", "staff", "Driver"],
    requiresPermission: "Orders",
  },

  {
    key: "product",
    path: "/product",
    icon: <Inventory2OutlinedIcon />,
    roles: ["admin", "sub_admin", "staff", "Driver"],
    requiresPermission: "Orders",
  },
  {
    key: "supplier-invites",
    path: "/supplier-invites",
    icon: <Inventory />,
    roles: [], // Empty roles - only shown for suppliers
    requiresPermission: null,
    showOnlyForSupplier: true, // Add this flag
  },
  {
    key: "alerts",
    path: "/alerts",
    icon: <ReportIcon />,
    roles: ["admin", "sub_admin", "staff", "Driver"],
    requiresPermission: "Orders",
  },
  {
    key: "reports",
    path: "/reports",
    icon: <ReportIcon />,
    roles: ["admin", "sub_admin", "staff", "supplier"],
    requiresPermission: "Reports",
    hideForSupplier: true,
  },
  {
    key: "customer",
    path: "/customer",
    icon: <GroupIcon />,
    roles: ["admin", "sub_admin", "staff", "Driver"],
    requiresPermission: "Customer",
  },

  {
    key: "user",
    path: "/user",
    icon: <ManageAccountsOutlinedIcon />,
    roles: ["admin", "sub_admin", "staff", "Driver"],
    requiresPermission: "Users",
    showOnlyForAdmin: true,
  },
  {
    key: "userPermissions",
    path: "/user-permissions",
    icon: <SecurityOutlinedIcon />,
    roles: ["admin", "sub_admin", "staff", "Driver"],
    requiresPermission: "Roles",
    showOnlyForAdmin: true,
  },
  {
    key: "pushNotifications",
    path: "/notifications",
    icon: <NotificationsNoneOutlinedIcon />,
    roles: ["admin", "sub_admin", "staff", "Driver"],
    requiresPermission: null,
    showOnlyForAdmin: true,
  },
  {
    key: "subscription",
    path: "/subscription",
    icon: <SubscriptionsIcon />,
    roles: ["admin", "sub_admin", "staff", "Driver"],
    requiresPermission: "Subscriptions",
  },

  {
    key: "dashboard",
    path: "/admin",
    icon: <AdminPanelSettingsOutlinedIcon />,
    roles: ["super_admin"],
  },
  {
    key: "company",
    path: "/admin/company",
    icon: <EmojiTransportationIcon />,
    roles: ["super_admin"],
  },
  {
    key: "plans",
    path: "/admin/plans",
    icon: <WorkspacePremiumOutlinedIcon />,
    roles: ["super_admin"],
  },
  {
    key: "enterprisePricing",
    path: "/admin/enterprise-pricing",
    icon: <RequestQuoteOutlinedIcon />,
    roles: ["super_admin"],
  },
  {
    key: "chat",
    path: "/chat",
    icon: <Chat />,
    roles: ["admin", "sub_admin", "staff", "Driver", "super_admin"],
    requiresPermission: null,
  },

  // {
  //   key: "subscription",
  //   path: "/admin/subscription",
  //   icon: <SubscriptionsIcon />,
  //   roles: ["super_admin"],
  // },
  {
    key: "settings",
    path: "/settings",
    icon: <SettingsOutlinedIcon />,
    roles: ["admin", "sub_admin", "staff", "Driver", "super_admin"],
    requiresPermission: null,
  },
];

const ORDER_GROUP_KEYS = ["order", "invoice", "customer"];

function Sidebar({
  open,
  drawerWidth,
  collapsedWidth,
  onToggleSidebar,
  variant,
  currentPath,
}) {
  const { t } = useTranslation();
  const { setUserLogout, user } = useUserStore();
  const { language } = useLocalizationStore();

  const role = user?.role?.role_type;
  const isSupplier = user?.is_supplier;

  const theme = useTheme();
  const logoTarget = useMemo(() => {
    if (isSupplier) return "/supplier-dashboard";
    if (role === "super_admin") return "/admin";
    return "/";
  }, [isSupplier, role]);

  const handleLogout = () => {
    setUserLogout();
  };

  const isPermanent = variant === "permanent";

  const paperWidth = isPermanent
    ? open
      ? drawerWidth
      : collapsedWidth
    : drawerWidth;

  const hasPermission = useCallback((permission) => {
    if (!permission) return true;
    return user?.role?.permissions.some(
      (perm) => perm.module === permission && perm.can_read
    );
  }, [user?.role?.permissions]);

  const filteredItems = useMemo(() => {
    return NAV_ITEMS.filter((item) => {
      if (isSupplier) {
        if (item.hideForSupplier) return false;
        if (item.showOnlyForSupplier) return true;
        if (item.showOnlyForFleetAdmin) return false;
        if (item.roles && item.roles.includes(role)) {
          if (
            item.requiresPermission &&
            !hasPermission(item.requiresPermission)
          ) {
            return false;
          }
          return true;
        }
        return false;
      }

      if (item.showOnlyForSupplier) return false;
      if (item.showOnlyForFleetAdmin) {
        if (role) return false;
        if (
          item.requiresPermission &&
          !hasPermission(item.requiresPermission)
        ) {
          return false;
        }
        return true;
      }
      if (item.showOnlyForAdmin) {
        if (role !== "admin") return false;
        if (
          item.requiresPermission &&
          !hasPermission(item.requiresPermission)
        ) {
          return false;
        }
        return true;
      }
      if (item.roles && item.roles.includes(role)) {
        if (
          item.requiresPermission &&
          !hasPermission(item.requiresPermission)
        ) {
          return false;
        }
        return true;
      }
      return false;
    });
  }, [hasPermission, isSupplier, role]);

  const isItemActive = (item) => {
    if (!item) return false;
    return item.path === "/"
      ? currentPath === "/"
      : currentPath === item.path || currentPath.startsWith(item.path + "/");
  };

  const [openChatSubmenu, setOpenChatSubmenu] = useState(false);
  const [openOrderSubmenu, setOpenOrderSubmenu] = useState(false);
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search || ""),
    [location.search]
  );
  const activeChatTab = useMemo(() => {
    if (location.pathname.includes("/chat/orders")) return "orders";
    if (location.pathname.includes("/chat/support")) return "support";
    return searchParams.get("tab") || "";
  }, [location.pathname, searchParams]);

  const chatSubItems = useMemo(() => {
    const allowedIds =
      role === "super_admin" ? ["support"] : ["support", "orders"];

    return chatTabs
      .filter((tab) => allowedIds.includes(tab.id))
      .map((tab) => ({
        ...tab,
        label: t(`chatTabs.${tab.id}`) || tab.label,
        path: tab.id === "support" ? "/chat/support" : "/chat/orders",
        icon:
          tab.id === "support" ? (
            <SupportAgentOutlinedIcon fontSize="small" />
          ) : (
            <AssignmentTurnedInOutlinedIcon fontSize="small" />
          ),
      }));
  }, [role, t]);
  const defaultChatTab = chatSubItems?.[0]?.id;

  useEffect(() => {
    if (currentPath.startsWith("/chat")) {
      setOpenChatSubmenu(true);
    }
  }, [currentPath]);

  useEffect(() => {
    if (
      currentPath.startsWith("/order") ||
      currentPath.startsWith("/invoice") ||
      currentPath.startsWith("/customer")
    ) {
      setOpenOrderSubmenu(true);
    }
  }, [currentPath]);

  const orderGroupItems = useMemo(
    () => filteredItems.filter((item) => ORDER_GROUP_KEYS.includes(item.key)),
    [filteredItems]
  );
  const navItems = useMemo(() => {
    const result = [];
    let orderGroupAdded = false;

    filteredItems.forEach((item) => {
      if (ORDER_GROUP_KEYS.includes(item.key)) {
        if (!orderGroupAdded) {
          result.push({ type: "orderGroup", items: orderGroupItems });
          orderGroupAdded = true;
        }
        return;
      }
      result.push({ type: "item", item });
    });

    return result;
  }, [filteredItems, orderGroupItems]);

  return (
    <Drawer
      variant={variant}
      open={isPermanent ? true : open}
      onClose={!isPermanent ? onToggleSidebar : undefined}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: paperWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: paperWidth,
          [language === "ar" ? "right" : "left"]: 0,
          boxSizing: "border-box",
          border: "none",
          background: theme.palette.background.paper,
          color: theme.palette.text.secondary,
          paddingTop: theme.spacing(3),
          paddingBottom: theme.spacing(3),
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        },
        display: {
          xs: isPermanent ? "none" : "block",
          md: isPermanent ? "block" : "none",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: open ? 3 : 0,
          pb: 3,
        }}
      >
        {open ? (
          <Box
            component={NavLink}
            to={logoTarget}
            onClick={!isPermanent ? onToggleSidebar : undefined}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              textDecoration: "none",
            }}
          >
            <Box
              component="img"
              src="/icon/Logo-colored.svg"
              alt="Mausool"
              style={{ height: 32 }}
            />
          </Box>
        ) : (
          <Box
            component={NavLink}
            to={logoTarget}
            onClick={!isPermanent ? onToggleSidebar : undefined}
            sx={{ display: "flex", textDecoration: "none" }}
          >
            <Box
              component="img"
              src="/icon/icon-colored.svg"
              alt="Mausool"
              style={{ height: 32 }}
            />
          </Box>
        )}
      </Box>
      <List sx={{ pl: 1.8, pr: 1.5 }}>
        {navItems.map((entry) => {
          if (entry.type === "orderGroup") {
            const parentItem =
              entry.items.find((item) => item.key === "order") ||
              entry.items[0] ||
              null;
            const isOrderActive = entry.items.some((item) =>
              isItemActive(item)
            );

            if (!parentItem) return null;

            return (
              <Box key="order-group" sx={{ mb: 1 }}>
                <ListItemButton
                  aria-label={t("nav.order")}
                  onClick={() => setOpenOrderSubmenu((prev) => !prev)}
                  sx={{
                    mb: 1,
                    borderRadius: "99px",
                    mx: open ? 1 : "auto",
                    // pl: open ? "14px" : 0,
                    justifyContent: open ? "flex-start" : "center",
                    color: theme.palette.text.secondary,
                    "&.Mui-selected, &.Mui-selected:hover": {
                      backgroundColor: theme.palette.primary.light,
                      color: theme.palette.primary.main,
                    },
                  }}
                  selected={isOrderActive}
                >
                  <ListItemIcon
                    sx={{
                      color: "inherit",
                      minWidth: 0,
                      mr: open ? 1 : 0,
                      justifyContent: "center",
                      transition: "margin 200ms ease",
                    }}
                  >
                    {parentItem.icon}
                  </ListItemIcon>
                  {open ? (
                    <ListItemText
                      primary={t("nav.order")}
                      primaryTypographyProps={{
                        variant: "body2",
                        fontWeight: 500,
                        color: "inherit",
                      }}
                    />
                  ) : null}
                  {open ? (
                    openOrderSubmenu ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )
                  ) : null}
                </ListItemButton>
                <Collapse
                  in={openOrderSubmenu || !open}
                  timeout="auto"
                  unmountOnExit
                >
                  {entry.items.map((subItem) => {
                    const selected = isItemActive(subItem);

                    const content = (
                      <ListItemButton
                        key={subItem.key}
                        aria-label={t(`nav.${subItem.key}`)}
                        component={NavLink}
                        to={subItem.path}
                        sx={{
                          mb: 0.75,
                          borderRadius: "99px",
                          mx: open ? 3 : "auto",
                          justifyContent: open ? "flex-start" : "center",
                          minHeight: 36,
                          // pl: open ? "14px" : 0,
                          color: theme.palette.text.secondary,
                          "&.Mui-selected, &.Mui-selected:hover": {
                            backgroundColor: theme.palette.primary.light,
                            color: theme.palette.primary.main,
                          },
                        }}
                        onClick={!isPermanent ? onToggleSidebar : undefined}
                        selected={selected}
                      >
                        <ListItemIcon
                          sx={{
                            color: "inherit",
                            minWidth: 0,
                            mr: open ? 1 : 0,
                            justifyContent: "center",
                            transition: "margin 200ms ease",
                          }}
                        >
                          {subItem.icon}
                        </ListItemIcon>
                        {open ? (
                          <ListItemText
                            primary={t(`nav.${subItem.key}`)}
                            primaryTypographyProps={{
                              variant: "body2",
                              fontWeight: 500,
                              color: "inherit",
                            }}
                          />
                        ) : null}
                      </ListItemButton>
                    );

                    if (open) {
                      return content;
                    }
                    return (
                      <Tooltip
                        key={subItem.key}
                        title={t(`nav.${subItem.key}`)}
                        placement="right"
                      >
                        {content}
                      </Tooltip>
                    );
                  })}
                </Collapse>
              </Box>
            );
          }

          const { item } = entry;
          const isChatItem = item.key === "chat";
          const isChatActive = currentPath.startsWith("/chat");

          if (isChatItem) {
            const parentSelected = isChatActive;
            return (
              <Box key={item.key} sx={{ mb: 1 }}>
                <ListItemButton
                  aria-label={t(`nav.${item.key}`)}
                  onClick={() => setOpenChatSubmenu((prev) => !prev)}
                  sx={{
                    mb: 1,
                    borderRadius: "99px",
                    mx: open ? 1 : "auto",
                    // pl: open ? "14px" : "8px",
                    justifyContent: open ? "flex-start" : "center",
                    color: theme.palette.text.secondary,
                    "&.Mui-selected, &.Mui-selected:hover": {
                      backgroundColor: theme.palette.primary.light,
                      color: theme.palette.primary.main,
                    },
                  }}
                  selected={parentSelected}
                >
                  <ListItemIcon
                    sx={{
                      color: "inherit",
                      minWidth: 0,
                      [language === "ar" ? "ml" : "mr"]: open ? 1 : 0,
                      justifyContent: "center",
                      transition: "margin 200ms ease",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {open ? (
                    <ListItemText
                      primary={t(`nav.${item.key}`)}
                      primaryTypographyProps={{
                        variant: "body2",
                        fontWeight: 500,
                        color: "inherit",
                      }}
                    />
                  ) : null}
                  {open ? (
                    openChatSubmenu ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )
                  ) : null}
                </ListItemButton>
                <Collapse
                  in={openChatSubmenu || !open}
                  timeout="auto"
                  unmountOnExit
                >
                  {chatSubItems.map((tab) => {
                    const selected =
                      isChatActive &&
                      (activeChatTab || defaultChatTab) === tab.id;

                    const content = (
                      <ListItemButton
                        key={tab.id}
                        aria-label={tab.label}
                        component={NavLink}
                        to={tab.path || `/chat/${tab.id}`}
                        sx={{
                          mb: 0.75,
                          borderRadius: "99px",
                          mx: open ? 3 : "auto",
                          justifyContent: open ? "flex-start" : "center",
                          minHeight: 36,
                          // pl: open ? "14px" : "8px",
                          color: theme.palette.text.secondary,
                          "&.Mui-selected, &.Mui-selected:hover": {
                            backgroundColor: theme.palette.primary.light,
                            color: theme.palette.primary.main,
                          },
                        }}
                        onClick={!isPermanent ? onToggleSidebar : undefined}
                        selected={selected}
                      >
                        <ListItemIcon
                          sx={{
                            color: "inherit",
                            minWidth: 0,
                            [language === "ar" ? "ml" : "mr"]: open ? 1 : 0,
                            justifyContent: "center",
                            transition: "margin 200ms ease",
                          }}
                        >
                          {tab.icon}
                        </ListItemIcon>
                        {open ? (
                          <ListItemText
                            primary={tab.label}
                            primaryTypographyProps={{
                              variant: "body2",
                              fontWeight: 500,
                              color: "inherit",
                            }}
                          />
                        ) : null}
                      </ListItemButton>
                    );

                    if (open) {
                      return content;
                    }
                    return (
                      <Tooltip key={tab.id} title={tab.label} placement="right">
                        {content}
                      </Tooltip>
                    );
                  })}
                </Collapse>
              </Box>
            );
          }

          const selected = isItemActive(item);

          const content = (
            <ListItemButton
              key={item.key}
              aria-label={t(`nav.${item.key}`)}
              component={NavLink}
              to={item.path}
              aria-current={currentPath === item.path ? "page" : undefined}
              sx={{
                mb: 1,
                borderRadius: "99px",
                mx: open ? 1 : "auto",
                [language === "ar" ? "pr" : "pl"]: open ? "14px" : "8px",
                color: theme.palette.text.secondary,
                "&.Mui-selected, &.Mui-selected:hover": {
                  backgroundColor: theme.palette.primary.light,
                  color: theme.palette.primary.main,
                },
              }}
              onClick={!isPermanent ? onToggleSidebar : undefined}
              selected={selected}
            >
              <ListItemIcon
                sx={{
                  color: "inherit",
                  minWidth: 0,
                  mr: open ? 1 : 0,

                  justifyContent: "center",
                  transition: "margin 200ms ease",
                }}
              >
                {item.icon}
              </ListItemIcon>
              {open ? (
                <ListItemText
                  primary={t(`nav.${item.key}`)}
                  primaryTypographyProps={{
                    variant: "body2",
                    fontWeight: 500,
                    color: "inherit",
                  }}
                />
              ) : null}
            </ListItemButton>
          );

          if (open) {
            return content;
          }
          return (
            <Tooltip
              key={item.key}
              title={t(`nav.${item.key}`)}
              placement="right"
            >
              {content}
            </Tooltip>
          );
        })}
      </List>
      {/* --- Logout Button (Bottom) --- */}
      <Box sx={{ p: 2, mt: "auto" }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: "99px",
            mx: open ? 1 : "auto",
            justifyContent: open ? "flex-start" : "center",
            background: theme.palette.background.paper,
            color: theme.palette.text.secondary,
            "&:hover": {
              // backgroundColor: theme.palette.error.light,
              // color: theme.palette.error.dark,
            },
          }}
        >
          <ListItemIcon
            sx={{
              color: "inherit",
              minWidth: 0,
              mr: open ? 1 : 0,
              justifyContent: "center",
            }}
          >
            <LogoutOutlined />
          </ListItemIcon>
          {open && (
            <ListItemText
              primary={t("Logout")}
              primaryTypographyProps={{
                variant: "body2",
                fontWeight: 500,
                color: "inherit",
              }}
            />
          )}
        </ListItemButton>
      </Box>
    </Drawer>
  );
}

Sidebar.propTypes = {
  open: PropTypes.bool.isRequired,
  drawerWidth: PropTypes.number.isRequired,
  collapsedWidth: PropTypes.number.isRequired,
  onToggleSidebar: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(["permanent", "temporary"]),
  currentPath: PropTypes.string,
};

Sidebar.defaultProps = {
  variant: "permanent",
  currentPath: "/",
};

export default Sidebar;
